/**
 * @module routes.Sessions.helpers.createPurchase
 */
import { DateTime } from "luxon";

import { schedulingFetchPost } from "app/utils/request/scheduling/fetch";
import { store } from "app/Store";

/**
 * Turn the current booking selection into a purchase and return where to go
 * next — the checkout summary for the purchase just created.
 *
 * Lifted out of SessionForm unchanged so the By Date screen can complete a
 * booking too: it has the same coach, option, day and time in the same place,
 * and duplicating this would be duplicating a payment call.
 *
 * Reads the store directly rather than taking the selection as arguments,
 * because callers dispatch immediately before confirming and a `useSelector`
 * value is still the previous render's.
 */
export interface PurchaseError {
  message?: string;
  detail?: string;
}

export const createPurchaseAndGetPath = async (
  onError?: (error: PurchaseError) => void,
): Promise<string | null> => {
  const currentState = store.getState().bookSession;

  if (!currentState.time || !currentState.coach) {
    console.error("Missing time or coach data for purchase");
    return null;
  }

  try {
    const payload: Record<string, unknown> = {
      start: DateTime.fromMillis(currentState.time.startHour)
        .setZone(currentState.coach.timeZone)
        .toISO(),
      end: DateTime.fromMillis(currentState.time.endHour)
        .setZone(currentState.coach.timeZone)
        .toISO(),
      coachProfileId: currentState.coach.id,
      lessonRateId: currentState.pricingValue?.id,
    };

    // Booking on behalf of a managed family member (CN-1323).
    if (currentState.participantAthleteId) {
      payload.participantAthleteId = currentState.participantAthleteId;
    }

    const data = await schedulingFetchPost(
      "/lesson_schedules/stripe_purchases",
      payload,
    );
    return `/purchase/${data.id}`;
  } catch (err) {
    console.error("Error creating purchase:", err);
    // The service refuses a slot that went to another booking (422). This
    // endpoint answers { errors: [{ message }] }. Other errors come as
    // { errors: [{ details: [{ message, detail }] }] }, as the edit
    // confirmation shows them.
    const first = (err as {
      errors?: { message?: string; details?: PurchaseError[] }[];
    })?.errors?.[0];
    onError?.(first?.details?.[0] || { detail: first?.message });
    return null;
  }
};
